import { Component, type ReactNode, type ErrorInfo } from 'react';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { redirect } from '@tanstack/react-router';
import { MenuDivider } from '../Components/BasicUI';
import Divider from '@mui/material/Divider';
import { faLightbulb, faEnvelope } from '@fortawesome/free-regular-svg-icons';
import { faPhone, faComment } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getApplicationData as _getApplicationData } from '../Redux/ApplicationActions';
import Images from '../Themes/Images';
import Colors from '../Themes/Colors';
import ErrorStyles from './Styles/ErrorStyles';
import { UserFeedbackLink } from '../Utils/appConstants';
import type { RootState } from '../Redux';
import type { FaStyleProp } from '../Utils/types';

type Props = {
  children: ReactNode;
  activeChatId?: number;
  getApplicationData: typeof _getApplicationData;
};

type State = {
  hasError: boolean;
  toChat: boolean;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      toChat: false,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { errorMsg: 'Error Boundary shown to the user', ...info },
      tags: { errorBoundary: 'true' },
    });
  }

  render() {
    const { getApplicationData, activeChatId } = this.props;

    const { hasError, toChat } = this.state;

    if (hasError) {
      if (toChat) {
        this.setState({
          hasError: false,
          toChat: false,
        });

        if (activeChatId) {
          getApplicationData(true, true, true, true, [activeChatId]);
        } else {
          getApplicationData(true, false, false, false, []);
        }

        throw redirect({ to: '/login' });
      }

      // NOTE: Any changes here need to be inline with official FlowHawk support policies
      // and should be mirrored in the ErrorBoundary handler for both TXP-Admin-Portal and TXPChat
      return (
        <div style={ErrorStyles.verticalCenter}>
          <div className="error-page-container">
            <div style={ErrorStyles.errorHeader}>
              <img style={ErrorStyles.errorHeaderImg} src={Images.xvivoSymbol} alt="" />
              Something unexpected occurred
            </div>
            <div style={ErrorStyles.outerWrapper}>
              <div style={ErrorStyles.innerWrapper}>
                <div style={ErrorStyles.errorTitle}>What you can do next</div>
                <MenuDivider />
                <div className="error-page-content">
                  <div className="error-page-message" style={ErrorStyles.errorMessage}>
                    <p style={ErrorStyles.errorMessageFirstLine}>
                      Please check your network connection for stability and sufficient speed. You can reload the
                      application with the button on this page, or try clearing your cache and recent browsing history
                      before refreshing your browser.
                    </p>
                    <p>
                      If the problem persists, please contact our support team directly through our official support
                      channels, found on our homepage. Thank you for your continued support and loyalty!
                    </p>
                  </div>
                  <Divider orientation="vertical" color={Colors.evenLighterGray} flexItem />
                  <div className="error-buttons-grid">
                    <a style={ErrorStyles.navButton} href="/chats" onClick={window.location.reload}>
                      <img style={ErrorStyles.icon} src={Images.xvivoSymbolSvg} alt="" />
                      <span style={ErrorStyles.buttonText}>&#x21bb; Reload the platform</span>
                    </a>
                    <a
                      style={ErrorStyles.navButton}
                      href={import.meta.env.VITE_LAUNCH_FAQ_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FontAwesomeIcon
                        fixedWidth
                        style={ErrorStyles.icon as FaStyleProp}
                        icon={faLightbulb}
                        color={Colors.darkGrayLogo}
                      />
                      <span style={ErrorStyles.buttonText}>Visit our Help Center &gt;</span>
                    </a>
                    <a
                      style={ErrorStyles.navButton}
                      href="mailto:support.digital@xvivogroup.com"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FontAwesomeIcon
                        fixedWidth
                        style={ErrorStyles.icon as FaStyleProp}
                        icon={faEnvelope}
                        color={Colors.darkGrayLogo}
                      />
                      <span style={ErrorStyles.buttonText}>Email&nbsp;support.digital@xvivogroup.com</span>
                    </a>
                    <a style={ErrorStyles.navButton} href="tel:+17208238396" target="_blank" rel="noreferrer">
                      <FontAwesomeIcon
                        fixedWidth
                        style={ErrorStyles.icon as FaStyleProp}
                        icon={faPhone}
                        color={Colors.darkGrayLogo}
                      />
                      <span style={ErrorStyles.buttonText}>Call&nbsp;720-823-8396, option 4</span>
                    </a>
                    <a style={ErrorStyles.navButton} href={UserFeedbackLink} target="_blank" rel="noreferrer">
                      <FontAwesomeIcon
                        fixedWidth
                        style={ErrorStyles.icon as FaStyleProp}
                        icon={faComment}
                        color={Colors.darkGrayLogo}
                      />
                      <span style={ErrorStyles.buttonText}>Give Feedback</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const mapStateToProps = (state: RootState) => ({
  activeChatId: state.chatList.activeChatId,
});

export default connect(mapStateToProps, {
  getApplicationData: _getApplicationData,
})(ErrorBoundary);
